import { current } from 'immer';
import { type DraftFunction, useImmer } from 'use-immer';

import { useChatSessionHistory } from './useChatSessionHistory';
import type { ChatProviderMessage, ChatSession } from '#components/chat/types';

export interface ChatSessionRef {
  session?: ChatSession;
  setTitle: (title: string) => void;
  setMessages: (messages: ChatProviderMessage[]) => void;
  addMessage: (message: ChatProviderMessage) => void;
}

export function useChatSession(id?: string): ChatSessionRef {
  const [chatSessionHistory, setChatSessionHistory] = useChatSessionHistory();
  const sessionId = id ?? crypto.randomUUID();

  const [session, setSession] = useImmer<ChatSession>(
    chatSessionHistory?.[sessionId] ?? createInitialSession(sessionId),
  );

  if (session?.id !== sessionId) {
    console.debug(
      `Loading chat session ID '${sessionId}' (${chatSessionHistory?.[sessionId] ? 'local storage' : 'create new'})`,
    );
    setSession(chatSessionHistory?.[sessionId] ?? createInitialSession(sessionId));
  }

  const updateSession: DraftFunction<ChatSession> = (draft) => {
    draft.updatedAt = new Date().toISOString();
    const currentSession = current(draft);
    console.debug('Current session changed, updating local storage', { session: currentSession });
    setChatSessionHistory((current) => ({
      ...current,
      [currentSession.id]: currentSession,
    }));
  };

  return {
    setTitle: (title) => {
      setSession((draft) => {
        draft.title = title;
        updateSession(draft);
      });
    },
    setMessages: (messages) => {
      setSession((draft) => {
        draft.messages.splice(0, draft.messages.length, ...messages);
        updateSession(draft);
      });
    },
    addMessage: (message) => {
      setSession((draft) => {
        draft.messages.push(message);
        updateSession(draft);
      });
    },
    session,
  };
}

const createInitialSession = (id: string = crypto.randomUUID()): ChatSession => ({
  id,
  messages: [],
  createdAt: new Date().toISOString(),
});
